import React, { useEffect } from "react"

import * as Sentry from "@sentry/react"

import { API_ORIGIN } from "@/lib/constants"
import { useCurrentUserQuery } from "@/src/graphql/public-generated-rq"
import { posthog } from "@/src/lib/posthog"

interface AuthContextData {
  isAuthenticated?: boolean
  isAdmin?: boolean
  pid?: string | null
  email?: string | null
  name?: string | null
  isImpersonated?: boolean
}

const AuthContext = React.createContext<AuthContextData | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data } = useCurrentUserQuery()

  const user = data?.currentUser

  const isAuthenticated = user?.isAuthenticated
  const isAdmin = user?.isAdmin
  const pid = user?.pid
  const email = user?.email
  const name = user?.name
  const isImpersonated = user?.isImpersonated

  useEffect(() => {
    if (!isAuthenticated || !pid) return

    posthog.identify(pid, {
      email: email ?? undefined,
      name: name ?? undefined,
      is_admin: isAdmin ?? undefined,
      is_impersonated: isImpersonated ?? undefined,
    })
    Sentry.setUser({
      id: pid,
      email: email ?? undefined,
      username: name ?? undefined,
    })
  }, [email, isAdmin, isAuthenticated, isImpersonated, name, pid])

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isAdmin, pid, email, name, isImpersonated }}
    >
      {children}
    </AuthContext.Provider>
  )
}

/*
 * Hook that provides access to authenticated user information.
 */
export function useAuth(): AuthContextData {
  const context = React.useContext(AuthContext)
  if (context === undefined) {
    console.warn("useAuth should be used within an AuthProvider")
    return {
      isAdmin: false,
      isAuthenticated: false,
    }
  }
  return context
}

/*
 * Render children only when the user is confirmed authenticated.
 * Redirect to /login if the user is not authenticated.
 */
export function AuthRequired({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (isAuthenticated === false) {
      const next = window.location
      location.href =
        API_ORIGIN +
        "/login?next=" +
        encodeURIComponent(next.pathname + next.search + next.hash)
    }
  }, [isAuthenticated])

  if (isAuthenticated === true) {
    return <>{children}</>
  }
  return null
}
